/**
 * phase-docx-preview — fetch the mammoth-rendered HTML for a .docx so the
 * preview pane can show Word content inline (Approach B). Mirrors
 * useInlineDocumentView's shape ({ html | loading | error }); the HTML is
 * rendered in a SANDBOXED iframe by the caller, never injected as markup.
 */
import { useEffect, useState } from 'react';
import { v2, ApiError } from '@/api/v2Client';

export interface DocxHtmlPreviewState {
  /** The rendered HTML document, or null while loading / on error / disabled. */
  readonly html: string | null;
  readonly loading: boolean;
  /** Honest failure text (never a raw response body), or null. */
  readonly error: string | null;
}

export function useDocxHtmlPreview(
  documentId: string | null,
  opts: { readonly enabled?: boolean } = {},
): DocxHtmlPreviewState {
  const enabled = opts.enabled !== false && documentId !== null;
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !documentId) {
      setHtml(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setHtml(null);
    setError(null);
    setLoading(true);
    v2.documents
      .previewHtml(documentId)
      .then((res) => {
        if (cancelled) return;
        setHtml(res.html);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError ? err.message : 'Could not render this document for preview.',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, enabled]);

  return { html, loading, error };
}
